import React, { useCallback, useState } from 'react';
import { Warning as WarningIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

export interface ConfirmationOptions {
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  severity?: 'warning' | 'error' | 'info';
}

export const useConfirmationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const confirm = useCallback((confirmationOptions: ConfirmationOptions): Promise<boolean> => {
    setOptions(confirmationOptions);
    setIsOpen(true);

    return new Promise<boolean>((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) {
      resolvePromise(true);
      setResolvePromise(null);
    }
  }, [resolvePromise]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
  }, [resolvePromise]);

  const ConfirmationDialog = useCallback(() => {
    if (!options) return null;

    const severityColor = options.severity === 'error' ? 'error' :
                         options.severity === 'warning' ? 'warning' : 'info';

    return (
      <Dialog
        open={isOpen}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {options.severity && (
              <WarningIcon color={severityColor} />
            )}
            <Typography variant="h6" component="span">
              {options.title}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {typeof options.message === 'string' ? (
            <Typography variant="body1">
              {options.message}
            </Typography>
          ) : (
            options.message
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCancel}
            variant="outlined"
          >
            {options.cancelText || 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={severityColor}
            autoFocus
          >
            {options.confirmText || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [isOpen, options, handleConfirm, handleCancel]);

  return {
    confirm,
    ConfirmationDialog,
  };
};